import Axios from "axios";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { APP_COOKIE_NAME, LOGOUT, SHOW_NOTIFICATION } from "../constants";
import { deleteCookie, getCookie, setCookie } from "../utils";

export const BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://PremodernMallorca-api.deploy.ecoquimic.com"
    : "http://localhost:3005";

export const axios = Axios.create({ baseURL: BASE_URL });

const isDev = process.env.NODE_ENV === "development";

const setAccessToken = async (token) => {
  const bearer = token ?? (await getCookie(APP_COOKIE_NAME));
  axios.defaults.headers.common = {
    Authorization: `Bearer ${bearer}`,
  };
  if (token != null && token !== "") {
    await setCookie(APP_COOKIE_NAME, token, 1);
  }
};

const attachAccessToken = async (request) => {
  if (request.headers?.Authorization) {
    return request;
  }
  const token = await getCookie(APP_COOKIE_NAME);
  if (token) {
    await setAccessToken(token);
    return {
      ...request,
      headers: { ...request.headers, Authorization: `Bearer ${token}` },
    };
  }
  return request;
};

const ConnectionHandler = () => {
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    const showNotification = (message = "HA OCURRIDO UN ERROR") => {
      dispatchRef.current({
        type: SHOW_NOTIFICATION,
        payload: { show: true, status: "error", message },
      });
    };

    const exitFromApp = () => {
      deleteCookie(APP_COOKIE_NAME);
      dispatchRef.current({ type: LOGOUT });
    };

    const requestId = axios.interceptors.request.use(
      async (request) => {
        if (isDev) {
          console.log(
            `[HTTP] ${request.method?.toUpperCase()} ${request.baseURL}${request.url}`
          );
        }
        return attachAccessToken(request);
      },
      (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Error de red";
        showNotification(msg);
        return Promise.reject(err);
      }
    );

    const responseId = axios.interceptors.response.use(
      async (response) => {
        if (
          response.config?.url === `/user/login` ||
          response.config?.url === `/user/check2FA`
        ) {
          await setAccessToken(response.data.access_token);
        }
        return response;
      },
      (err) => {
        const {
          status = 0,
          data: { message = "" } = {},
          config: { url = "" } = {},
        } = err.response || {};

        if (isDev) {
          console.warn(`[HTTP ${status}] ${url}: ${message}`);
        }

        switch (status) {
          case 401:
            if (message === "Invalid username or password") {
              showNotification(`Usuario o contraseña incorrectos.`);
              exitFromApp();
              return Promise.reject(err);
            }
            if (message !== "Invalid token") showNotification(`${message}`);
            exitFromApp();
            return Promise.reject(err);
          case 300:
            return Promise.reject(err);
          default:
            break;
        }
        showNotification(`${message}`);
        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, []);

  return null;
};

export default ConnectionHandler;
